/**
 * Bottle Tracking - Admin Dashboard
 * Features:
 * - View customer bottle balances
 * - Transaction history
 * - Manual adjustments
 * - Analytics & reports
 * - CSV export
 */

import { useState, useEffect } from 'react';
import { 
  Milk, TrendingUp, TrendingDown, DollarSign, Users, Download,
  Search, Calendar, Filter, Plus, FileText, BarChart3, RefreshCw,
  ChevronLeft, ChevronRight, AlertCircle, CheckCircle, X, Phone
} from 'lucide-react';
import { bottleTrackingAPI } from '../services/deliverySystemAPI';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';

const BottleTracking = () => {
  const { hasPermission } = useAuth();
  const canEdit = hasPermission('orders_edit');

  // State
  const [activeTab, setActiveTab] = useState('balances'); // balances, transactions, analytics
  const [balances, setBalances] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(false);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const itemsPerPage = 50;

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [dateRange, setDateRange] = useState({
    startDate: getDateDaysAgo(30),
    endDate: getTodayDate()
  });
  const [transactionTypeFilter, setTransactionTypeFilter] = useState('all');

  // Modals
  const [showAdjustmentModal, setShowAdjustmentModal] = useState(false);
  const [adjustmentData, setAdjustmentData] = useState({
    customerUuid: '',
    adjustmentType: 'add',
    quantity: 0,
    reason: ''
  });

  // Load data on mount and tab change
  useEffect(() => {
    loadData();
  }, [activeTab, currentPage, searchTerm, dateRange, transactionTypeFilter]);

  // Helper functions
  function getTodayDate() {
    return new Date().toISOString().split('T')[0];
  }

  function getDateDaysAgo(days) {
    const date = new Date();
    date.setDate(date.getDate() - days);
    return date.toISOString().split('T')[0];
  }

  // Load data based on active tab
  const loadData = async () => {
    try {
      setLoading(true);
      
      if (activeTab === 'balances') {
        await loadBalances();
      } else if (activeTab === 'transactions') {
        await loadTransactions();
      } else if (activeTab === 'analytics') {
        await loadAnalytics();
      }
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  // Load customer balances
  const loadBalances = async () => {
    try {
      const response = await bottleTrackingAPI.getCustomerBalances({
        page: currentPage,
        limit: itemsPerPage,
        search: searchTerm
      });
      
      const data = response.data.data;
      setBalances(data.balances || []);
      setTotalPages(data.pagination?.totalPages || 1);
      setTotalItems(data.pagination?.total || 0);
    } catch (error) {
      console.error('Error loading balances:', error);
      toast.error('Failed to load balances');
    }
  };

  // Load transactions
  const loadTransactions = async () => {
    try {
      const params = {
        page: currentPage,
        limit: itemsPerPage,
        startDate: dateRange.startDate,
        endDate: dateRange.endDate
      };
      
      if (transactionTypeFilter !== 'all') {
        params.transactionType = transactionTypeFilter;
      }
      
      const response = await bottleTrackingAPI.getTransactions(params);
      
      const data = response.data.data;
      setTransactions(data.transactions || []);
      setTotalPages(data.pagination?.pages || 1);
      setTotalItems(data.pagination?.total || 0);
    } catch (error) {
      console.error('Error loading transactions:', error);
      toast.error('Failed to load transactions');
    }
  };

  // Load analytics
  const loadAnalytics = async () => {
    try {
      const response = await bottleTrackingAPI.getAnalytics(
        dateRange.startDate,
        dateRange.endDate
      );
      setAnalytics(response.data.data);
    } catch (error) {
      console.error('Error loading analytics:', error);
      toast.error('Failed to load analytics');
    }
  };

  // Handle manual adjustment
  const handleCreateAdjustment = async () => {
    if (!canEdit) {
      toast.error('You do not have permission to create adjustments');
      return;
    }

    if (!adjustmentData.customerUuid || !adjustmentData.quantity || !adjustmentData.reason) {
      toast.error('Please fill all required fields');
      return;
    }

    try {
      await bottleTrackingAPI.createManualAdjustment(
        adjustmentData.customerUuid,
        adjustmentData.adjustmentType,
        parseInt(adjustmentData.quantity),
        adjustmentData.reason
      );
      
      toast.success('Adjustment created successfully!');
      setShowAdjustmentModal(false);
      setAdjustmentData({
        customerUuid: '',
        adjustmentType: 'add',
        quantity: 0,
        reason: ''
      });
      loadData();
    } catch (error) {
      console.error('Error creating adjustment:', error);
      toast.error(error.response?.data?.message || 'Failed to create adjustment');
    }
  };

  // Export to CSV
  const handleExport = async () => {
    try {
      toast.loading('Generating CSV...', { id: 'export' });
      const response = await bottleTrackingAPI.exportToCSV(
        dateRange.startDate,
        dateRange.endDate
      );
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `bottle-report-${dateRange.startDate}-to-${dateRange.endDate}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('CSV downloaded successfully!', { id: 'export' });
    } catch (error) {
      console.error('Error exporting:', error);
      toast.error('Failed to export data', { id: 'export' });
    }
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Bottle Tracking</h1>
          <p className="text-gray-600 mt-1">Manage customer bottle balances and transactions</p>
        </div>
        <div className="flex gap-2">
          {activeTab !== 'analytics' && (
            <button
              onClick={handleExport}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            >
              <Download className="w-4 h-4" />
              Export CSV
            </button>
          )}
          <button
            onClick={loadData}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="border-b border-gray-200">
          <nav className="flex -mb-px">
            <button
              onClick={() => {
                setActiveTab('balances');
                setCurrentPage(1);
              }}
              className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'balances'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center gap-2">
                <Users className="w-4 h-4" />
                Customer Balances
              </div>
            </button>
            <button
              onClick={() => {
                setActiveTab('transactions');
                setCurrentPage(1);
              }}
              className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'transactions'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center gap-2">
                <FileText className="w-4 h-4" />
                Transaction History
              </div>
            </button>
            <button
              onClick={() => {
                setActiveTab('analytics');
              }}
              className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'analytics'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center gap-2">
                <BarChart3 className="w-4 h-4" />
                Analytics & Reports
              </div>
            </button>
          </nav>
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {loading ? (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
          ) : (
            <>
              {activeTab === 'balances' && (
                <BalancesTab
                  balances={balances}
                  searchTerm={searchTerm}
                  onSearchChange={setSearchTerm}
                  onAdjustment={() => setShowAdjustmentModal(true)}
                  canEdit={canEdit}
                />
              )}
              
              {activeTab === 'transactions' && (
                <TransactionsTab
                  transactions={transactions}
                  dateRange={dateRange}
                  onDateRangeChange={setDateRange}
                  transactionTypeFilter={transactionTypeFilter}
                  onTransactionTypeChange={setTransactionTypeFilter}
                />
              )}
              
              {activeTab === 'analytics' && analytics && (
                <AnalyticsTab
                  analytics={analytics}
                  dateRange={dateRange}
                  onDateRangeChange={setDateRange}
                />
              )}
            </>
          )}

          {/* Pagination */}
          {activeTab !== 'analytics' && totalPages > 1 && (
            <div className="flex items-center justify-between mt-6 pt-6 border-t border-gray-200">
              <div className="text-sm text-gray-600">
                Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, totalItems)} of {totalItems} results
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-1 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <span className="px-4 py-1 text-sm text-gray-700">
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Manual Adjustment Modal */}
      {showAdjustmentModal && (
        <Modal
          title="Create Manual Adjustment"
          onClose={() => setShowAdjustmentModal(false)}
        >
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Customer UUID
              </label>
              <input
                type="text"
                value={adjustmentData.customerUuid}
                onChange={(e) => setAdjustmentData({ ...adjustmentData, customerUuid: e.target.value })}
                placeholder="Enter customer UUID"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Adjustment Type
              </label>
              <select
                value={adjustmentData.adjustmentType}
                onChange={(e) => setAdjustmentData({ ...adjustmentData, adjustmentType: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="add">Add Bottles</option>
                <option value="remove">Remove Bottles</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Quantity
              </label>
              <input
                type="number"
                min="0"
                value={adjustmentData.quantity}
                onChange={(e) => setAdjustmentData({ ...adjustmentData, quantity: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Reason
              </label>
              <textarea
                value={adjustmentData.reason}
                onChange={(e) => setAdjustmentData({ ...adjustmentData, reason: e.target.value })}
                rows="3"
                placeholder="Enter reason for adjustment..."
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div className="flex gap-3 justify-end pt-4">
              <button
                onClick={() => setShowAdjustmentModal(false)}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateAdjustment}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Create Adjustment
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

// Balances Tab Component
const BalancesTab = ({ balances, searchTerm, onSearchChange, onAdjustment, canEdit }) => {
  const bottleSizes = ['500ml', '1L'];
  
  return (
    <div className="space-y-4">
      {/* Search & Actions */}
      <div className="flex gap-4">
        <div className="flex-1 relative">
          <Search className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
          <input
            type="text"
            placeholder="Search by name, phone, or email..."
            value={searchTerm}
            onChange={(e) => onSearchChange(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          />
        </div>
      </div>

      {/* Balances Cards */}
      {balances.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <Milk className="w-16 h-16 mx-auto mb-4 text-gray-400" />
          <p>No customer balances found</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-1 gap-4">
          {balances.map((customer) => (
            <div key={customer.customer_uuid} className="bg-white rounded-lg border border-gray-200 p-4 hover:shadow-lg transition-shadow">
              {/* Customer Info */}
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="text-lg font-bold text-gray-900">{customer.customer_name}</h3>
                  <div className="flex items-center gap-4 mt-2 text-sm text-gray-600">
                    {customer.customer_phone && (
                      <a href={`tel:${customer.customer_phone}`} className="flex items-center gap-1 text-blue-600 hover:text-blue-700">
                        <Phone className="w-4 h-4" />
                        {customer.customer_phone}
                      </a>
                    )}
                    {customer.customer_email && (
                      <a href={`mailto:${customer.customer_email}`} className="flex items-center gap-1 text-blue-600 hover:text-blue-700">
                        {customer.customer_email}
                      </a>
                    )}
                  </div>
                </div>
                {/* Total Pending Badge */}
                <div className="text-right">
                  <div className="text-2xl font-bold text-red-600">🥛 {customer.total_pending_bottles}</div>
                  <p className="text-xs text-gray-600 mt-1">Pending</p>
                </div>
              </div>

              {/* Bottle Size Breakdown */}
              <div className="grid grid-cols-3 gap-2 mb-4">
                {bottleSizes.map(size => {
                  const count = customer.bottle_balances_detail?.[size] || 0;
                  return (
                    <div key={size} className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-3 text-center">
                      <p className="text-xs font-semibold text-blue-900">{size}</p>
                      <p className="text-2xl font-bold text-blue-600 mt-1">{count}</p>
                    </div>
                  );
                })}
              </div>

              {/* Last Updated */}
              <div className="text-xs text-gray-500 border-t pt-3">
                Last updated: {customer.last_updated ? new Date(customer.last_updated).toLocaleString() : 'N/A'}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Transactions Tab Component
const TransactionsTab = ({ transactions, dateRange, onDateRangeChange, transactionTypeFilter, onTransactionTypeChange }) => {
  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex flex-wrap gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Start Date</label>
          <input
            type="date"
            value={dateRange.startDate}
            onChange={(e) => onDateRangeChange({ ...dateRange, startDate: e.target.value })}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">End Date</label>
          <input
            type="date"
            value={dateRange.endDate}
            onChange={(e) => onDateRangeChange({ ...dateRange, endDate: e.target.value })}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Transaction Type</label>
          <select
            value={transactionTypeFilter}
            onChange={(e) => onTransactionTypeChange(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">All Types</option>
            <option value="given">Given</option>
            <option value="collected">Collected</option>
            <option value="broken">Broken</option>
            <option value="manual_add">Manual Add</option>
            <option value="manual_remove">Manual Remove</option>
          </select>
        </div>
      </div>

      {/* Transactions Table */}
      {transactions.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          No transactions found for the selected filters
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b-2 border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Date</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Customer</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Type</th>
                <th className="px-4 py-3 text-center text-sm font-semibold text-gray-900">Quantity</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Order</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Notes</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {transactions.map((txn, index) => (
                <tr key={index} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm text-gray-900">
                    {new Date(txn.transactionDate).toLocaleString()}
                  </td>
                  <td className="px-4 py-3">
                    <div>
                      <p className="text-sm font-medium text-gray-900">{txn.customerName}</p>
                      <p className="text-xs text-gray-500">{txn.customerPhone}</p>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                      txn.transactionType === 'given' ? 'bg-blue-100 text-blue-800' :
                      txn.transactionType === 'collected' ? 'bg-green-100 text-green-800' :
                      txn.transactionType === 'broken' ? 'bg-red-100 text-red-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {(txn.transactionType || '').replace('_', ' ').toUpperCase()}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center font-semibold text-gray-900">{txn.quantity}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{txn.orderNumber || 'N/A'}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{txn.notes || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// Analytics Tab Component
const AnalyticsTab = ({ analytics, dateRange, onDateRangeChange }) => {
  return (
    <div className="space-y-6">
      {/* Date Range Selector */}
      <div className="flex gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Start Date</label>
          <input
            type="date"
            value={dateRange.startDate}
            onChange={(e) => onDateRangeChange({ ...dateRange, startDate: e.target.value })}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">End Date</label>
          <input
            type="date"
            value={dateRange.endDate}
            onChange={(e) => onDateRangeChange({ ...dateRange, endDate: e.target.value })}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          />
        </div>
      </div>

      {/* Summary Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-blue-50 p-6 rounded-lg border-2 border-blue-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-blue-600 font-medium">Total Given</p>
              <p className="text-3xl font-bold text-blue-900">{analytics.summary.totalGiven}</p>
            </div>
            <TrendingUp className="w-10 h-10 text-blue-500" />
          </div>
        </div>

        <div className="bg-green-50 p-6 rounded-lg border-2 border-green-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-green-600 font-medium">Total Collected</p>
              <p className="text-3xl font-bold text-green-900">{analytics.summary.totalCollected}</p>
            </div>
            <CheckCircle className="w-10 h-10 text-green-500" />
          </div>
        </div>

        <div className="bg-red-50 p-6 rounded-lg border-2 border-red-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-red-600 font-medium">Total Broken</p>
              <p className="text-3xl font-bold text-red-900">{analytics.summary.totalBroken}</p>
            </div>
            <AlertCircle className="w-10 h-10 text-red-500" />
          </div>
        </div>

        <div className="bg-yellow-50 p-6 rounded-lg border-2 border-yellow-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-yellow-600 font-medium">Pending Bottles</p>
              <p className="text-3xl font-bold text-yellow-900">{analytics.summary.pendingBottles}</p>
            </div>
            <Milk className="w-10 h-10 text-yellow-500" />
          </div>
        </div>
      </div>

      {/* Delivery Boy Performance */}
      {analytics.byDeliveryBoy && analytics.byDeliveryBoy.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h3 className="text-lg font-bold text-gray-900 mb-4">Delivery Boy Performance</h3>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b-2 border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Delivery Boy</th>
                  <th className="px-4 py-3 text-center text-sm font-semibold text-gray-900">Bottles Given</th>
                  <th className="px-4 py-3 text-center text-sm font-semibold text-gray-900">Bottles Collected</th>
                  <th className="px-4 py-3 text-center text-sm font-semibold text-gray-900">Bottles Broken</th>
                  <th className="px-4 py-3 text-center text-sm font-semibold text-gray-900">Transactions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {analytics.byDeliveryBoy.map((boy, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-medium text-gray-900">{boy.deliveryBoyName}</td>
                    <td className="px-4 py-3 text-center text-gray-900">{boy.bottlesGiven}</td>
                    <td className="px-4 py-3 text-center text-gray-900">{boy.bottlesCollected}</td>
                    <td className="px-4 py-3 text-center text-gray-900">{boy.bottlesBroken}</td>
                    <td className="px-4 py-3 text-center text-gray-900">{boy.transactionCount}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

// Modal Component
const Modal = ({ title, children, onClose }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
        <div className="flex justify-between items-center p-6 border-b border-gray-200">
          <h3 className="text-xl font-bold text-gray-900">{title}</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>
        <div className="p-6">
          {children}
        </div>
      </div>
    </div>
  );
};

export default BottleTracking;